import { useQueryClient } from '@tanstack/react-query';
import { MapPinIcon, RefreshCwIcon } from 'lucide-react';
import { useCallback } from 'react';
import { useParams } from 'react-router';

import { LocationType } from '@invenco/common-interface/accounts';
import type { LocationDTO } from '@invenco/common-interface/accounts';

import { Button } from '@/components/core/button';
import { DetailGroup } from '@/components/detail-group';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, DetailSide, Page, PageContent } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { useEntityDetailsQuery, useQueryWithInput } from '@/shared/hooks/queries';

import { LocationDetailsSection } from './location-details-section';
import { ManagedLocationsCard } from './managed-locations-card';
import { ShippingMethodsCard } from './shipping-methods-card';
import { WarehouseDetailsSection } from './warehouse-details-section';

export function LocationDetails() {
  const { id } = useParams<{ id: string }>();
  const { accountsGateway, shippingGateway } = useGateways();
  const queryClient = useQueryClient();

  const {
    data: location,
    isLoading: isLoadingLocation,
    refetch: refetchLocation,
  } = useEntityDetailsQuery<LocationDTO>({
    parentKey: 'locations',
    id,
    isNew: !id,
    query: (locationId, { signal }) => accountsGateway.getLocation(locationId, { signal }),
  });

  const {
    data: shippingMethods = [],
    isLoading: isLoadingShippingMethods,
    refetch: refetchShippingMethods,
  } = useQueryWithInput({
    parentKey: 'shippingMethods',
    input: { locationId: id },
    query: (input, { signal }) => shippingGateway.getShippingMethods(input, { signal }),
    select: ({ items }) => items,
    enabled: Boolean(id),
  });

  const refresh = useCallback(async () => {
    if (!id) {
      return;
    }

    await Promise.all([
      refetchLocation(),
      refetchShippingMethods(),
      queryClient.invalidateQueries({ queryKey: ['warehouses', { locationId: id }] }),
      queryClient.invalidateQueries({ queryKey: ['locations', { warehouseLocationId: id }] }),
    ]);
  }, [id, queryClient, refetchLocation, refetchShippingMethods]);

  const isWarehouse = location?.type === LocationType.WAREHOUSE;

  return (
    <Page>
      <Header loading={isLoadingLocation}>
        <HeaderIcon parentUrl="/locations" parentTitle="Locations">
          <MapPinIcon />
        </HeaderIcon>
        <HeaderTitle>{location?.name}</HeaderTitle>
        <HeaderActions>
          <Button
            size="icon"
            variant="outline"
            aria-label="Refresh"
            onClick={() => void refresh()}
            disabled={isLoadingLocation}
          >
            <RefreshCwIcon />
          </Button>
        </HeaderActions>
      </Header>
      <PageContent variant="split">
        <DetailMain>
          {isWarehouse ? <ManagedLocationsCard locationId={id} /> : null}
          <ShippingMethodsCard
            shippingMethods={shippingMethods}
            isLoading={isLoadingShippingMethods}
            locationType={location?.type}
            locationId={id}
            accountId={location?.accountId}
            warehouseLocationId={location?.warehouseLocationId}
          />
        </DetailMain>
        <DetailSide>
          <DetailGroup>
            <LocationDetailsSection location={location} isLoading={isLoadingLocation} />
            {isWarehouse ? <WarehouseDetailsSection locationId={id} /> : null}
          </DetailGroup>
        </DetailSide>
      </PageContent>
    </Page>
  );
}
